import { inject } from '@angular/core';
import { CanActivateChildFn, CanActivateFn, Router } from '@angular/router';

import { NotificationAccess } from './notification-access';

/**
 * The notifications ask, on the way into the app.
 *
 * Runs LAST in the /tabs chain - after the account is onboarded and the club
 * joined - so the first thing anyone is asked for is who they are, not what
 * the app may send them. The SAME question is asked by `Landing` after a
 * sign-in or refresh (that road covers admins, whose landing never passes
 * this chain); this guard remains for deep links straight into /tabs.
 *
 * It only diverts when the system will actually show its sheet:
 *
 * - **granted** - nothing to do, and nothing to say about it.
 * - **denied** - the OS has already been asked and said no; on iOS it will
 *   never ask again, so a page whose button cannot do anything would be a
 *   dead end. Turning them back on is a Settings job, and belongs on the
 *   app's own settings screen rather than in the doorway.
 * - **unsupported** - the web build. No device permission exists to ask for.
 * - **skipped** - "not now", for this session (see NotificationAccess).
 *
 * A plugin that throws is treated as unsupported by the seam, so a broken
 * permission read can never lock somebody out of their club.
 */
export const notificationsGuard: CanActivateFn & CanActivateChildFn = async () => {
  const access = inject(NotificationAccess);
  const router = inject(Router);

  // The check itself is a data-init loader (data-init.ts) and Landing asks
  // the same question after a sign-in - this guard is the catch-all for
  // DEEP LINKS straight into /tabs, so both roads ask identically.
  await access.whenChecked();
  return access.shouldAsk() ? router.parseUrl('/enable-notifications') : true;
};
